
import { Component, Vue, Watch } from 'vue-property-decorator'
import Empty from '@/components/Empty/index.vue'
import { UserModule } from '@/store/modules/user'
import ImageUpload from '@/components/ImgUpload/index.vue'
import { listMyStores } from '@/api/store'
import {
  getHomeBannerList,
  createHomeBanner,
  updateHomeBanner,
  deleteHomeBanner,
  homeBannerSetSort,
  AppHomeBanner
} from '@/api/homeBanner'
import { deleteImage } from '@/api/common'
import SortInlineEdit from '@/components/SortInlineEdit/index.vue'

@Component({
  name: 'HomeBanner',
  components: {
    Empty,
    ImageUpload,
    SortInlineEdit
  }
})
export default class extends Vue {
  private tableData: AppHomeBanner[] = []
  private searchTitle: string = ''
  private searchStatus: number | null = null
  private isSearch: boolean = false
  private storeOptions: Array<{ id: number; name: string }> = []

  private dialogVisible: boolean = false
  private form: AppHomeBanner = {
    title: '',
    imageUrl: '',
    linkUrl: '',
    status: 1,
    sort: 0,
    storeId: null
  }

  private imageUrl: string = ''

  private rules: any = {
    storeId: [{ required: true, message: '请先在顶栏选择商户', trigger: 'change' }],
    imageUrl: [{ required: true, message: '请上传轮播图片', trigger: 'change' }],
    status: [{ required: true, message: '请选择状态', trigger: 'change' }]
  }

  private imageChange(url: string) {
    this.form.imageUrl = url
    this.imageUrl = url
  }

  get currentStoreId() {
    const sid = UserModule.storeId
    if (!sid) return null
    const n = Number(sid)
    return Number.isNaN(n) ? null : n
  }

  get currentStoreLabel(): string {
    const sid = this.currentStoreId
    if (sid == null) return '未选择商户'
    return this.getStoreName(sid)
  }

  @Watch('currentStoreId')
  private onStoreChange() {
    this.loadList()
  }

  created() {
    this.loadStores()
    this.loadList()
  }

  private async loadStores() {
    try {
      const res: any = await listMyStores()
      if (String(res.data.code) === '1' && Array.isArray(res.data.data)) {
        this.storeOptions = res.data.data.map((it: any) => ({
          id: it.id,
          name: it.storeName || it.name || `商户${it.id}`
        }))
      } else {
        this.storeOptions = []
      }
    } catch (e) {
      this.storeOptions = []
    }
  }

  private getStoreName(id?: number | null) {
    if (id == null) return '未选择'
    const found = this.storeOptions.find((s) => Number(s.id) === Number(id))
    return found ? found.name : `商户${id}`
  }

  private async loadList() {
    const storeId = this.currentStoreId
    if (!storeId) {
      this.tableData = []
      return
    }
    const params: any = {
      storeId,
      status: this.searchStatus !== null ? this.searchStatus : undefined
    }
    this.isSearch = !!this.searchTitle || this.searchStatus !== null

    try {
      const res: any = await getHomeBannerList(params)
      if (res.data.code === 1) {
        let list: AppHomeBanner[] = res.data.data || []
        if (this.searchTitle) {
          const kw = this.searchTitle.trim()
          list = list.filter((row) => (row.title || '').indexOf(kw) !== -1)
        }
        this.tableData = list.filter(
          (row) => row && row.storeId != null && Number(row.storeId) === storeId
        )
      }
    } catch (err) {
      this.$message.error('请求出错了：' + err.message)
    }
  }

  private setSortHandle(row: any, sort: number) {
    if (!row || !row.id) return
    homeBannerSetSort({ id: row.id, sort })
      .then((res: any) => {
        if (res && res.data && String(res.data.code) === '1') {
          this.$message.success('排序已更新')
          this.loadList()
        } else {
          this.$message.error((res.data && (res.data.msg || res.data.desc)) || '排序失败')
        }
      })
      .catch((err: any) => {
        this.$message.error('请求出错了：' + ((err && err.message) || '未知错误'))
      })
  }

  private openDialog(row?: AppHomeBanner) {
    const storeId = this.currentStoreId
    if (!storeId) {
      this.$message.warning('请先在顶栏选择商户')
      return
    }
    if (row) {
      this.form = { ...row, storeId }
      this.imageUrl = row.imageUrl || ''
    } else {
      this.form = {
        title: '',
        imageUrl: '',
        linkUrl: '',
        status: 1,
        sort: null as any,
        storeId
      }
      this.imageUrl = ''
    }
    this.dialogVisible = true
    this.$nextTick(() => {
      const form: any = this.$refs.formRef
      if (form && typeof form.clearValidate === 'function') {
        form.clearValidate()
      }
    })
  }

  private handleSubmit() {
    this.form.imageUrl = this.imageUrl
    ;(this.$refs.formRef as any).validate(async (valid: boolean) => {
      if (!valid) return
      const storeId = this.currentStoreId
      if (!storeId) {
        this.$message.warning('请先在顶栏选择商户')
        return
      }
      if (!this.imageUrl) {
        this.$message.warning('请上传轮播图片')
        return
      }
      try {
        const payload: AppHomeBanner = { ...this.form, storeId, imageUrl: this.imageUrl }
        if (!payload.id) {
          delete (payload as any).sort
        }
        if (payload.id) {
          await updateHomeBanner(payload)
        } else {
          await createHomeBanner(payload)
        }
        this.$message.success('保存成功')
        this.dialogVisible = false
        this.loadList()
      } catch (err) {
        this.$message.error('保存失败：' + err.message)
      }
    })
  }

  private handleDelete(row: AppHomeBanner) {
    this.$confirm('确认删除该轮播图吗？', '提示', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async () => {
      try {
        if (row.id) {
          const imageUrl = (row.imageUrl || '').trim()
          await deleteHomeBanner(row.id)
          // 后端会删 MinIO；前端再兜底一次，避免旧版后端未清理
          if (imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))) {
            try {
              await deleteImage(imageUrl)
            } catch (e) {
              console.warn('轮播图 MinIO 清理失败:', e)
            }
          }
          this.$message.success('删除成功')
          this.loadList()
        }
      } catch (err) {
        this.$message.error('删除失败：' + err.message)
      }
    })
  }
}
